import type {
  ForwardedRef,
  ForwardRefExoticComponent,
  ComponentProps,
  ReactElement,
} from 'react';
import { forwardRef, useContext } from 'react';
import styled from '@emotion/styled';
import type { Interpolation, PropsOf, Theme } from '@emotion/react';
import type { Placement } from 'tippy.js';
import * as tokens from '@tokens';
import { Icon } from '@components/Icon/Icon';
import type { IconName } from '@components/Icon/BaseIcon';
import { Loader } from '@components/Loader/Loader';
import { Tooltip } from '@components/Tooltip/Tooltip';
import type {
  ButtonOrLinkProps,
  ButtonOrLinkElement,
} from '@common/ButtonOrLink';
import { externalRel } from '@common/ButtonOrLink';
import { shouldForwardProp } from '@utils/styledProps';
import { HotsauceContext } from '@providers/hotsauceContext';
import { navigationClickHandler } from '@utils/navigation-handler';
import {
  baseStyles,
  secondaryStyles,
  ghostStyles,
  dangerGhostStyles,
  dangerSecondaryStyles,
} from './styles';
import type { BaseIcon } from '@components/Icon/BaseIcon';
import { useStrings } from '@hooks/useStrings';

export type ButtonKind =
  | 'secondary'
  | 'ghost'
  | 'danger_secondary'
  | 'danger_ghost';

export type IconButtonProps<
  T extends ButtonOrLinkElement = ButtonOrLinkElement,
> = ButtonOrLinkProps<T> & {
  /**
   * Accessible button description visible only to screen readers.
   */
  description?: string;
  /**
   * Icon to be shown in the button.
   * It should be used as in the form of the icon name.
   */
  icon: IconName | ReactElement<ComponentProps<typeof BaseIcon>, 'BaseIcon'>;
  /**
   * Should the button returns a loading state. Only applies when `as="button"`
   */
  isLoading?: T extends 'button' ? boolean : undefined;
  /**
   * Kind of button used.
   */
  kind?: ButtonKind;
  /**
   * Size of the button.
   */
  size?: 's' | 'm';
  /**
   * Text content of the tooltip. Use tooltips to show the action taken on an icon button, e.g. "Add attribute".
   */
  tooltipText: string;
  /**
   * Text content of the tooltip in loading state. Only applies when
   * `as="button"`
   */
  loadingTooltipText?: T extends 'button' ? string : undefined;
  /**
   * Preferred placement of the tooltip (default: top)
   */
  tooltipPlacement?: Placement;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Styles override for the button element
   */
  buttonStyles?: Interpolation<Theme>;
};

const BUTTON_SIZE = {
  s: 32,
  m: 40,
};

const BUTTON_KIND_STYLES = {
  secondary: secondaryStyles,
  ghost: ghostStyles,
  danger_secondary: dangerSecondaryStyles,
  danger_ghost: dangerGhostStyles,
};

/**
 * List of props to use in the styled component
 * @private
 */
const styledIconButtonPropNames = ['kind', 'size', 'isLoading'] as const;

const StyledButton = styled('button', {
  shouldForwardProp: shouldForwardProp(styledIconButtonPropNames),
})<Partial<IconButtonProps>>`
  ${baseStyles};
  height: ${({ size }) => BUTTON_SIZE[size]}px;
  width: ${({ size }) => BUTTON_SIZE[size]}px;

  ${({ kind }) => BUTTON_KIND_STYLES[kind]};

  &[disabled] {
    cursor: not-allowed;
    background-color: transparent;
    ${tokens.elevation(0)};
    color: ${({ isLoading }) =>
      isLoading ? tokens.color.text_default : tokens.color.text_disabled};
  }
`;

const StyledLink = styled('a', {
  shouldForwardProp: shouldForwardProp(styledIconButtonPropNames),
})<Partial<IconButtonProps>>`
  ${baseStyles};
  height: ${({ size }) => BUTTON_SIZE[size]}px;
  width: ${({ size }) => BUTTON_SIZE[size]}px;

  ${({ kind }) => BUTTON_KIND_STYLES[kind]};
`;

const ButtonWrapper = styled('span')`
  display: inline-flex;
`;

type PassedIconButtonProps<T extends ButtonOrLinkElement> = Omit<
  IconButtonProps<T>,
  | 'as'
  | 'icon'
  | 'description'
  | 'className'
  | 'tooltipText'
  | 'tooltipPlacement'
>;

const IconElement = ({ icon }: Pick<IconButtonProps, 'icon'>): JSX.Element => {
  if (icon instanceof Object) {
    return icon;
  }

  return icon && <Icon icon={icon as IconName} size="m" />;
};

/**
 * Render function for IconButton component
 * @see IconButton
 * @private
 */
const IconButtonRender = <T extends ButtonOrLinkElement = ButtonOrLinkElement>(
  {
    as,
    icon,
    className,
    tooltipText,
    tooltipPlacement,
    description,
    buttonStyles,
    ...props
  }: IconButtonProps<T>,
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
): JSX.Element => {
  const { onNavigate } = useContext(HotsauceContext) ?? {};
  const { actions } = useStrings();

  // link case
  if (as === 'a') {
    const { isExternal, rel, ...linkProps } =
      props as PassedIconButtonProps<'a'>;
    const onClick = (event) =>
      navigationClickHandler({
        event,
        isExternal,
        linkProps: linkProps as PropsOf<'a'>,
        onNavigate,
      });

    return (
      <ButtonWrapper className={className}>
        <Tooltip text={tooltipText} placement={tooltipPlacement}>
          <StyledLink
            {...linkProps}
            ref={ref as ForwardedRef<HTMLAnchorElement>}
            rel={isExternal ? externalRel(rel) : undefined}
            target={isExternal ? '_blank' : undefined}
            aria-label={description}
            onClick={onClick}
            {...(buttonStyles ? { css: buttonStyles } : {})}
          >
            <IconElement icon={icon} />
          </StyledLink>
        </Tooltip>
      </ButtonWrapper>
    );
  }

  const { isDisabled, isLoading, type, loadingTooltipText, ...buttonProps } =
    props as PassedIconButtonProps<'button'>;

  const finalLoadingTooltipText = loadingTooltipText || actions.loading;

  const Button = (
    <StyledButton
      {...buttonProps}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={type || 'button'}
      aria-label={description}
      disabled={isDisabled || isLoading}
      isLoading={isLoading}
      {...(buttonStyles ? { css: buttonStyles } : {})}
    >
      {isLoading ? <Loader isGreyscale /> : <IconElement icon={icon} />}
    </StyledButton>
  );

  if (isDisabled || isLoading) {
    return (
      // Disabled buttons don't trigger tooltips so we apply tooltip to the wrapper.
      <Tooltip
        text={isLoading ? finalLoadingTooltipText : tooltipText}
        placement={tooltipPlacement}
      >
        <ButtonWrapper className={className}>{Button}</ButtonWrapper>
      </Tooltip>
    );
  }
  return (
    <ButtonWrapper className={className}>
      <Tooltip text={tooltipText} placement={tooltipPlacement}>
        {Button}
      </Tooltip>
    </ButtonWrapper>
  );
};

/**
 * Clickable icon used to perform actions.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/342b9f-icon-button/b/940add Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-iconbutton--basic-button Code documentation}
 *
 * @example
 * <IconButton icon="bell" onClick={() => {}} />
 */
export const IconButton = forwardRef(
  IconButtonRender,
) as ForwardRefExoticComponent<IconButtonProps>;

IconButton.defaultProps = {
  as: 'button',
  tooltipPlacement: 'top',
  size: 'm',
  kind: 'secondary',
  isDisabled: false,
  isLoading: false,
};

IconButton.displayName = 'IconButton';
